import { useAtomValue, useSetAtom } from "jotai";
import { useCallback, useEffect, useState } from "react";

import {
  editorTabAtom,
  loadProjectEditorAtom,
  projectEditorAtom,
  projectForm,
  saveProjectAtom,
} from "../stores/editor";
import type { AmendmentImpact, ProjectType } from "../types";
import { usePendingClosureWeeks } from "./usePendingClosureWeeks";

export type UseProjectEditorOptions = {
  projectId?: number | null;
  type?: ProjectType;
  subscriptionId?: number | null;
  onSaved?: (projectId: number) => void;
  onCancel?: () => void;
};

export type UseProjectEditor = {
  loading: boolean;
  saving: boolean;
  error: string | null;
  conflict: boolean;
  dirty: boolean;
  save: () => Promise<void>;
  cancel: () => void;
  impact: {
    open: boolean;
    amendments: AmendmentImpact[];
    remaining: number;
    onConfirm: () => void;
    onCancel: () => void;
  };
};

export function useProjectEditor({
  projectId = null,
  type = "intervention",
  subscriptionId = null,
  onSaved,
  onCancel,
}: UseProjectEditorOptions): UseProjectEditor {
  const { loading, saving, error, conflict } = useAtomValue(projectEditorAtom);
  const formDirty = useAtomValue(projectForm.state.isDirty);

  const closureWeeks = usePendingClosureWeeks(projectId);

  const load = useSetAtom(loadProjectEditorAtom);
  const saveProject = useSetAtom(saveProjectAtom);
  const setTab = useSetAtom(editorTabAtom);

  useEffect(() => {
    void load(projectId, type, subscriptionId);
  }, [load, projectId, type, subscriptionId]);

  const [confirming, setConfirming] = useState(false);

  const runSave = useCallback(async () => {
    if (!(await closureWeeks.save())) {
      setTab("closure-weeks");

      return;
    }

    const savedId = await saveProject();

    if (null !== savedId) {
      onSaved?.(savedId);
    }
  }, [closureWeeks, saveProject, setTab, onSaved]);

  const save = useCallback(async () => {
    if (0 < closureWeeks.impacts.length || 0 < closureWeeks.remaining) {
      setConfirming(true);

      return;
    }

    await runSave();
  }, [closureWeeks.impacts.length, closureWeeks.remaining, runSave]);

  const confirmSave = useCallback(() => {
    setConfirming(false);

    void runSave();
  }, [runSave]);

  const cancel = useCallback(() => onCancel?.(), [onCancel]);

  return {
    loading,
    saving,
    error,
    conflict,
    dirty: formDirty || closureWeeks.dirty,
    save,
    cancel,
    impact: {
      open: confirming,
      amendments: closureWeeks.impacts,
      remaining: closureWeeks.remaining,
      onConfirm: confirmSave,
      onCancel: () => setConfirming(false),
    },
  };
}
